
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle,
  Users,
  TrendingUp,
  Shield,
  Handshake,
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  AlertCircle
} from 'lucide-react';
import BecomeMemberHero from '../components/BecomeMemberHero';
import PageBackground from '../components/PageBackground';

const BecomeMember = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FileText className="w-5 h-5" /> },
    { id: 'benefits', label: 'Benefits', icon: <Star className="w-5 h-5" /> },
    { id: 'process', label: 'Application Process', icon: <Clock className="w-5 h-5" /> },
    { id: 'requirements', label: 'Requirements', icon: <CheckCircle className="w-5 h-5" /> }
  ];

  const detailedBenefits = [
    {
      category: "Financial Services",
      items: [
        "Access to low-interest agricultural loans",
        "Savings and investment programs",
        "Insurance coverage for crops and equipment",
        "Financial planning and advisory services"
      ]
    },
    {
      category: "Business Support",
      items: [
        "Bulk purchasing of inputs at discounted rates",
        "Marketing and distribution assistance",
        "Technical training and workshops",
        "Equipment sharing and rental programs"
      ]
    },
    {
      category: "Community & Networking",
      items: [
        "Regular member meetings and events",
        "Knowledge sharing with experienced farmers",
        "Access to agricultural experts and consultants",
        "Participation in cooperative decision-making"
      ]
    },
    {
      category: "Market Access",
      items: [
        "Collective bargaining for better prices",
        "Access to premium markets and buyers",
        "Quality certification and standards",
        "Export and international market opportunities"
      ]
    }
  ];

  const applicationSteps = [
    {
      step: 1,
      title: "Submit Application",
      description: "Complete the online application form with your personal and business information",
      duration: "5-10 minutes",
      icon: <FileText className="w-6 h-6 text-blue-600" />
    },
    {
      step: 2,
      title: "Document Review",
      description: "Our team will review your application and required documents",
      duration: "3-5 business days",
      icon: <CheckCircle className="w-6 h-6 text-green-600" />
    },
    {
      step: 3,
      title: "Interview & Assessment",
      description: "Meet with our membership committee for a brief interview",
      duration: "1-2 weeks",
      icon: <Users className="w-6 h-6 text-purple-600" />
    },
    {
      step: 4,
      title: "Approval & Onboarding",
      description: "Receive approval and complete your membership setup",
      duration: "1-2 weeks",
      icon: <Star className="w-6 h-6 text-yellow-600" />
    }
  ];

  const detailedRequirements = [
    {
      category: "Personal Requirements",
      items: [
        "Must be at least 18 years of age",
        "Filipino citizen or permanent resident",
        "Good moral character and reputation",
        "Willing to participate in cooperative activities"
      ]
    },
    {
      category: "Business Requirements",
      items: [
        "Involved in agriculture, farming, or related business",
        "Operating within our service area",
        "Demonstrated commitment to sustainable practices",
        "Willing to share knowledge and resources"
      ]
    },
    {
      category: "Financial Requirements",
      items: [
        "Ability to pay initial share capital (₱1,000)",
        "Regular contribution to savings program",
        "Financial stability and good credit standing",
        "Commitment to long-term membership"
      ]
    },
    {
      category: "Documentation Required",
      items: [
        "Valid government-issued ID",
        "Proof of residence and business location",
        "Business registration or farming permit",
        "Recent financial statements or income proof"
      ]
    }
  ];

  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      label: "Phone",
      value: "+63 912 345 6789",
      link: "tel:+639123456789"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      label: "Email",
      value: "membership@ppac-coop.ph",
      link: "mailto:membership@ppac-coop.ph"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      label: "Office",
      value: "123 Cooperative St., City Center",
      link: "#"
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      label: "Office Hours",
      value: "Mon-Fri: 8:00 AM - 5:00 PM",
      link: "#"
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'benefits':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Comprehensive Member Benefits
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Discover how PPAC Agricultural Cooperative empowers its members with 
                comprehensive support and exclusive opportunities.
              </p>
            </div>
            
                           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 {detailedBenefits.map((category, index) => (
                   <div key={index} className="card p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    {category.category === "Financial Services" && <Shield className="w-6 h-6 text-green-600 mr-3" />}
                    {category.category === "Business Support" && <TrendingUp className="w-6 h-6 text-blue-600 mr-3" />}
                    {category.category === "Community & Networking" && <Users className="w-6 h-6 text-purple-600 mr-3" />}
                    {category.category === "Market Access" && <Handshake className="w-6 h-6 text-orange-600 mr-3" />}
                    {category.category}
                  </h3>
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'process':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Simple 4-Step Application Process
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our streamlined application process makes it easy to join our cooperative. 
                Here's what you can expect from start to finish.
              </p>
            </div>
            
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               {applicationSteps.map((step, index) => (
                 <div key={index} className="card p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-xl font-bold text-green-600">{step.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-sm text-green-600 font-medium">{step.duration}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <div className="flex justify-center">
                    {step.icon}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                Ready to Get Started?
              </h3>
              <p className="text-blue-700 mb-4">
                The entire process typically takes 3-4 weeks from application to approval.
              </p>
              <Link
                to="/become-member/apply"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Begin Your Application
              </Link>
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Membership Requirements & Documentation
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                To ensure the success of our cooperative and maintain high standards, 
                we have specific requirements for all potential members.
              </p>
            </div>
            
                         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
               {detailedRequirements.map((category, index) => (
                 <div key={index} className="card p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                    {category.category}
                  </h3>
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    Important Note
                  </h3>
                  <p className="text-yellow-800">
                    All requirements must be met and verified before approval. 
                    Incomplete applications may delay the process. If you have questions 
                    about any requirements, please contact our membership team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default: // overview
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose PPAC Agricultural Cooperative?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We're more than just a cooperative – we're a community of agricultural 
                professionals working together to build a sustainable future.
              </p>
            </div>

                         <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-green-600 mr-3" />
                  Our Mission
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To empower agricultural professionals through cooperative principles, 
                  providing access to resources, knowledge, and markets while promoting 
                  sustainable farming practices and community development.
                </p>
              </div>

                             <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Star className="w-6 h-6 text-yellow-600 mr-3" />
                  Our Vision
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To be the leading agricultural cooperative in the region, recognized 
                  for innovation, member success, and positive impact on rural communities 
                  and the agricultural sector.
                </p>
              </div>
            </div>

                         <div className="bg-gradient-to-r from-surface to-moss-50 border border-border rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Join Our Growing Community
              </h3>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Become part of a network of successful agricultural professionals who 
                have transformed their businesses and communities through cooperation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/become-member/apply"
                  className="inline-flex items-center px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  Start Application
                </Link>
                <Link
                  to="/become-member/benefits"
                  className="inline-flex items-center px-8 py-3 bg-white text-green-700 font-semibold rounded-lg border-2 border-green-200 hover:border-green-300 hover:bg-green-50 transition-colors duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Hero Section */}
      <BecomeMemberHero showApplyButton={false} />

      {/* Main Content wrapped in PageBackground */}
      <PageBackground edgeToEdge={true}>
        {/* Navigation Tabs */}
        <div className="card mb-8">
          <div className="flex flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-green-100 text-green-700 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="card p-8">
          {renderTabContent()}
        </div>

        {/* Contact Section */}
        <div className="mt-12 card p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Help? Contact Us
            </h2>
            <p className="text-lg text-gray-600">
              Our membership team is here to answer your questions and guide you through the process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((contact, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    {contact.icon}
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{contact.label}</h3>
                {contact.link !== "#" ? (
                  <a
                    href={contact.link}
                    className="text-green-600 hover:text-green-700 transition-colors duration-200"
                  >
                    {contact.value}
                  </a>
                ) : (
                  <p className="text-gray-600">{contact.value}</p>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              Have specific questions about membership?
            </p>
            <Link
              to="/become-member/apply"
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              Start Your Application
            </Link>
          </div>
        </div>
      </PageBackground>
    </div>
  );
};

export default BecomeMember;
